import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { enableObservabilityAiTestError, vegaArtifactGraph } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ContextualFeedbackModal } from '@gonfalon/feedback/ContextualFeedbackModal';
import { toDiagnosticUsage, toHref, toSecurity, toVegaSettings } from '@gonfalon/navigator/navigators';
import {
  useGetConversationHistoryLazyQuery,
  useGetVegaIntegrationStatusQuery,
  useShareConversationMutation,
} from '@gonfalon/observability-graphql/hooks';
import { canonicalizeArtifactExternalId } from '@gonfalon/observability-ui/components/Artifact';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { Button } from '@gonfalon/observability-ui/components/Button';
import { Dialog } from '@gonfalon/observability-ui/components/Dialog';
import { EnableSettingModal } from '@gonfalon/observability-ui/components/EnableSettingModal';
import { IconSolidX } from '@gonfalon/observability-ui/components/icons';
import {
  ArtifactLookupProvider,
  type ArtifactLookupEntry,
} from '@gonfalon/observability-ui/components/MarkdownRenderer';
import { AI_BOT_NAME } from '@gonfalon/observability-ui/constants';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';
import {
  Alert,
  Heading,
  toastQueue,
  Button as LaunchpadButton,
  Menu as LDMenu,
  MenuItem,
  MenuTrigger,
  Popover,
  Text,
  Tooltip,
  TooltipTrigger,
  IconButton,
  DialogTrigger,
  Dialog as LaunchpadDialog,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { useAgentInsights } from '../../AgentInsightsContext';
import type { ConversationData } from '../../AgentInsightsContext';
import { writeTextToClipboard } from '../../copyTextToClipboard';
import { useAiChatLimit } from '../../hooks/useAiChatLimit';
import {
  trackVegaAiConversationCleared,
  trackVegaAiConversationLoaded,
  trackVegaAiConversationShared,
  trackVegaAiPanelClosed,
  trackVegaAiPanelOpened,
} from '../../vegaAnalytics';

import { AgentInsights } from './AgentInsights';
import type { AgentPromptMutationFn } from './AgentInsights';
import { ConversationHistoryView } from './ConversationHistoryView';

import * as styles from './AgentInsightsPanel.css';

// Stable empty list — inline `[]` would invalidate ArtifactLookupProvider's useMemo each render.
const EMPTY_ARTIFACT_LOOKUP: ArtifactLookupEntry[] = [];

export interface AgentInsightsPanelProps {
  nonGenAiSkill?: boolean;
  agentPromptMutation: AgentPromptMutationFn;
  sending: boolean;
  enableAgentResponseSelector?: boolean;
}

export const AgentInsightsPanel: React.FC<AgentInsightsPanelProps> = ({
  nonGenAiSkill = false,
  agentPromptMutation,
  sending,
  enableAgentResponseSelector = false,
}) => {
  const {
    isOpen,
    close,
    loadConversation,
    currentConversationId,
    conversationIdToLoad,
    open: openPanel,
    panelWidth,
    setPanelWidth,
    maxPanelWidth,
    setIsDraggingPanel,
    setLivePanelWidth,
  } = useAgentInsights();
  const { account, profile } = useAccountContext();
  const navigate = useNavigate();
  const { projectId, projectKey } = useProjectId();
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const clearRef = useRef<(() => void) | null>(null);
  const focusRef = useRef<(() => void) | null>(null);
  const clearPromptRef = useRef<(() => void) | null>(null);
  const enableAiTestError = enableObservabilityAiTestError();
  const [shouldThrowError, setShouldThrowError] = useState(false);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [conversationNotShared, setConversationNotShared] = useState(false);
  const [showAiRedirectModal, setShowAiRedirectModal] = useState(false);
  const [showingFeedback, setShowingFeedback] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [getConversationHistory] = useGetConversationHistoryLazyQuery();
  const [shareConversation] = useShareConversationMutation();

  const goToSettings = useCallback(() => {
    close();
    navigate(toHref(toVegaSettings({ projectKey })));
  }, [close, navigate, projectKey]);

  const clearUrlParameter = useCallback((paramName: string) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(paramName);
    window.history.replaceState({}, '', url.toString());
  }, []);

  const setUrlParameter = useCallback((paramName: string, paramValue: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set(paramName, paramValue);
    window.history.replaceState({}, '', url.toString());
  }, []);

  const { data: aiChatLimitData } = useAiChatLimit();

  const { error: vegaPermissionError } = useGetVegaIntegrationStatusQuery({
    variables: { project_id: projectId },
    skip: !projectId,
  });

  const retrieveConversationFromNetwork = useCallback(
    async (conversationId: string) => {
      setIsLoadingConversation(true);
      setConversationNotShared(false);
      try {
        const { data, error } = await getConversationHistory({
          variables: { conversation_id: conversationId },
          fetchPolicy: 'network-only',
        });

        if (error) {
          if (error.message?.includes('not been shared')) {
            setConversationNotShared(true);
          }
          throw error;
        }

        const response = data?.conversation_history;
        const messages = response?.messages || [];

        if (messages.length > 0) {
          const conversationData: ConversationData = {
            conversationId,
            status: response?.status || 'active',
            messages,
          };

          loadConversation(conversationData);
        }

        return messages;
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error('Error retrieving conversation history:', error);
        throw error;
      } finally {
        setIsLoadingConversation(false);
      }
    },
    [getConversationHistory, loadConversation],
  );

  const handleOpenHistoryConversation = useCallback(
    (conversationId: string) => {
      setShowHistory(false);
      retrieveConversationFromNetwork(conversationId)
        .then(() => {
          trackVegaAiConversationLoaded(conversationId);
        })
        .catch((error: unknown) => {
          // eslint-disable-next-line no-console
          console.error('Failed to load conversation from history:', error);
        });
    },
    [retrieveConversationFromNetwork],
  );

  const handleShare = useCallback(async () => {
    if (!currentConversationId) {
      return;
    }
    try {
      // Attempt to mark the conversation as shared. This will succeed for
      // conversations owned by the current user and gracefully fail for
      // conversations owned by others (which are already shared).
      await shareConversation({
        variables: {
          project_id: projectId,
          conversation_id: currentConversationId,
        },
      });
    } catch {
      // Ignore share mutation errors — the conversation may already be
      // shared or owned by another user. We still want to copy the link.
    }
    trackVegaAiConversationShared(currentConversationId);
    if (await writeTextToClipboard(window.location.href)) {
      toastQueue.add({ status: 'info', title: 'Link copied to clipboard' });
    } else {
      toastQueue.add({ status: 'error', title: 'Failed to copy link' });
    }
  }, [currentConversationId, shareConversation, projectId]);

  // Throw error during render phase so error boundary can catch it
  if (shouldThrowError) {
    console.log('A very bad thing happened! (just kidding this is a test error)');
    throw new Error('A very bad thing happened! (just kidding this is a test error)');
  }

  const dialogStore = Dialog.useStore({
    open: isOpen && (nonGenAiSkill || account.isGeneralGenAiEnabled),
    setOpen: (open) => {
      if (!open) {
        trackVegaAiPanelClosed({
          conversationId: currentConversationId || undefined,
        });
        close();
      }
    },
  });

  // Show modal instead of panel if AI is not enabled (skipped when the
  // consumer opts out of the gen-AI gate, e.g. account-scoped skill panels).
  useEffect(() => {
    if (nonGenAiSkill) {
      return;
    }
    if (isOpen && !account.isGeneralGenAiEnabled) {
      setShowAiRedirectModal(true);
      close(); // Close the panel request
    }
  }, [isOpen, account.isGeneralGenAiEnabled, close, nonGenAiSkill]);

  // Track panel opened when isOpen changes to true
  useEffect(() => {
    if (isOpen) {
      trackVegaAiPanelOpened({
        conversationId: currentConversationId || undefined,
      });
    }
  }, [isOpen]); // Only depend on isOpen, not currentConversationId

  const updateWidth = useCallback(
    (newWidth: number) => {
      const clampedWidth = Math.min(Math.max(newWidth, 20), maxPanelWidth);

      if (dialogRef.current) {
        dialogRef.current.style.setProperty('--panel-width', `${clampedWidth}%`);
      }

      return clampedWidth;
    },
    [maxPanelWidth],
  );

  const dragWidthRef = useRef(panelWidth);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragging.current) {
        return;
      }

      e.preventDefault();

      const newWidth = 100 - (e.clientX / window.innerWidth) * 100;
      dragWidthRef.current = updateWidth(newWidth);
      setLivePanelWidth(dragWidthRef.current);
    },
    [updateWidth, setLivePanelWidth],
  );

  const handleMouseUp = useCallback(() => {
    dragging.current = false;
    setIsDraggingPanel(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    setPanelWidth(dragWidthRef.current);
    setLivePanelWidth(dragWidthRef.current);
  }, [handleMouseMove, setPanelWidth, setIsDraggingPanel, setLivePanelWidth]);

  const handleMouseDown = useCallback(() => {
    dragging.current = true;
    setIsDraggingPanel(true);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove, handleMouseUp, setIsDraggingPanel]);

  useEffect(() => {
    const dragHandle = dragHandleRef.current;
    if (dragHandle) {
      dragHandle.addEventListener('mousedown', handleMouseDown);
      return () => {
        dragHandle.removeEventListener('mousedown', handleMouseDown);
      };
    }
  }, [handleMouseDown]);

  // Initialize panel width from persisted value, clamped to max
  useEffect(() => {
    if (isOpen) {
      const clamped = updateWidth(panelWidth);
      setLivePanelWidth(clamped);
      if (clamped !== panelWidth) {
        setPanelWidth(clamped);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on open/max change, not when panelWidth updates
  }, [isOpen, updateWidth]);

  // Check for AgentConversationId URL parameter on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const agentConversationId = urlParams.get('AgentConversationId');
    const showVega = urlParams.get('showVega');
    const configureAgent = urlParams.get('ConfigureAgent');

    // Old Slack alert-investigation messages embed `?ConfigureAgent=true`
    // buttons that previously opened a settings view inline. The settings UI
    // now lives at /projects/:projectKey/vega/settings, so redirect those
    // buttons rather than dropping users into an empty Vega panel.
    if (configureAgent === 'true' && projectKey) {
      navigate(toVegaSettings({ projectKey }), { replace: true });
      return;
    }

    if (agentConversationId) {
      openPanel({
        context: {
          resourceOrQuery: [],
        },
      });

      // Load the conversation (user can continue it)
      retrieveConversationFromNetwork(agentConversationId)
        .then(() => {
          trackVegaAiConversationLoaded(agentConversationId);
        })
        .catch((error: unknown) => {
          // eslint-disable-next-line no-console
          console.error('Failed to load conversation from URL parameter:', error);
        });
    } else if (showVega === '1') {
      openPanel({
        context: {
          resourceOrQuery: [],
        },
      });
    }
  }, [retrieveConversationFromNetwork, openPanel, navigate, projectKey]);

  // Load conversation when conversationIdToLoad is provided (for button clicks)
  useEffect(() => {
    if (conversationIdToLoad) {
      retrieveConversationFromNetwork(conversationIdToLoad)
        .then(() => {
          trackVegaAiConversationLoaded(conversationIdToLoad);
        })
        .catch((error: unknown) => {
          // eslint-disable-next-line no-console
          console.error('Failed to load conversation:', error);
        });
    }
  }, [conversationIdToLoad, retrieveConversationFromNetwork]);

  // Automatically sync URL parameter with panel state and conversation ID
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlConversationId = urlParams.get('AgentConversationId');

    // Add parameter when panel is open and has a conversation ID
    if (isOpen && currentConversationId) {
      // Only update if different to avoid unnecessary history updates
      if (urlConversationId !== currentConversationId) {
        setUrlParameter('AgentConversationId', currentConversationId);
      }
    }
    // Remove parameter when panel is closed or there's no active conversation
    else if (urlConversationId && (!isOpen || !currentConversationId)) {
      clearUrlParameter('AgentConversationId');
    }

    if (isOpen) {
      setUrlParameter('showVega', '1');
    } else {
      clearUrlParameter('showVega');
    }
  }, [isOpen, currentConversationId, setUrlParameter, clearUrlParameter]);

  // Auto-focus textarea when panel opens
  useEffect(() => {
    if (isOpen && focusRef.current) {
      setTimeout(() => {
        focusRef.current?.();
      }, 100);
    }
  }, [isOpen]);

  const renderPanelHeader = () => {
    if (showHistory) {
      return (
        <>
          <Box display="flex" alignItems="center" py="8" gap="8">
            <Icon name="clock" size="small" className={styles.sparklesIcon} />
            <span className={styles.panelTitle}>Your {AI_BOT_NAME} history</span>
          </Box>
          <Box display="flex" alignItems="center" gap="8">
            <TooltipTrigger>
              <Button
                kind="secondary"
                emphasis="low"
                size="small"
                aria-label="Close history"
                onClick={() => setShowHistory(false)}
                className={styles.iconButton}
              >
                <IconSolidX />
              </Button>
              <Tooltip placement="bottom">Close history</Tooltip>
            </TooltipTrigger>
          </Box>
        </>
      );
    }

    return (
      <>
        <Box display="flex" alignItems="center" py="8" gap="8">
          <Icon name="sparkles" size="small" className={styles.sparklesIcon} />
          <span className={styles.panelTitle}>{AI_BOT_NAME} AI</span>
          <DialogTrigger>
            <IconButton
              variant="minimal"
              size="small"
              icon="info-circle"
              aria-label="Information about Vega AI"
              className={styles.infoButton}
            />
            <Popover>
              <LaunchpadDialog className={styles.infoDialog}>
                <Text slot="subtitle">
                  {AI_BOT_NAME} is a suite of observability AI tools which has been enabled for your organization.{' '}
                  {AI_BOT_NAME} is currently offered to select customers as a Free Trial Service. {AI_BOT_NAME} can be
                  enabled or disabled in your{' '}
                  <a
                    href={toHref(toSecurity({ hash: 'enable-genai' }))}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.inlineLink}
                  >
                    security settings
                  </a>
                  .
                </Text>
              </LaunchpadDialog>
            </Popover>
          </DialogTrigger>
        </Box>
        <Box display="flex" alignItems="center" gap="8">
          <MenuTrigger>
            <LaunchpadButton variant="minimal" aria-label="More options" className={styles.iconButton}>
              <Icon name="more-vert" size="small" />
            </LaunchpadButton>
            <Popover placement="bottom end">
              <LDMenu
                onAction={(action) => {
                  switch (action) {
                    case 'share':
                      handleShare();
                      break;
                    case 'send-feedback':
                      setShowingFeedback(true);
                      break;
                    case 'vega-settings':
                      goToSettings();
                      break;
                    case 'test-error':
                      setShouldThrowError(true);
                      break;
                    default:
                      break;
                  }
                }}
              >
                <MenuItem id="share" isDisabled={!currentConversationId}>
                  <Text slot="label">
                    <Icon name="link" size="small" /> Share
                  </Text>
                </MenuItem>
                <MenuItem id="send-feedback">
                  <Text slot="label">
                    <Icon name="chat-bubble-outline" size="small" /> Send Feedback
                  </Text>
                </MenuItem>
                <MenuItem id="vega-settings">
                  <Text slot="label">
                    <Icon name="gear" size="small" /> {AI_BOT_NAME} settings
                  </Text>
                </MenuItem>
                {enableAiTestError && (
                  <MenuItem id="test-error">
                    <Text slot="label">
                      <Icon name="warning" size="small" /> Test Error
                    </Text>
                  </MenuItem>
                )}
              </LDMenu>
            </Popover>
          </MenuTrigger>

          <TooltipTrigger>
            <LaunchpadButton
              variant="default"
              size="small"
              aria-label="History"
              onPress={() => setShowHistory(true)}
              className={styles.iconButton}
            >
              <Icon name="clock" size="small" />
            </LaunchpadButton>
            <Tooltip placement="bottom">History</Tooltip>
          </TooltipTrigger>

          <LaunchpadButton
            variant="default"
            size="small"
            onPress={() => {
              trackVegaAiConversationCleared({
                conversationId: currentConversationId || undefined,
              });
              setConversationNotShared(false);
              setShowHistory(false);
              clearRef.current?.();
              clearPromptRef.current?.();
            }}
            className={styles.newChatButton}
          >
            <Icon name="add" size="small" /> New chat
          </LaunchpadButton>

          <Box borderLeft="dividerWeak" cssClass={styles.headerDivider} />

          <Button kind="secondary" emphasis="low" size="small" onClick={close} className={styles.iconButton}>
            <IconSolidX />
          </Button>
        </Box>
      </>
    );
  };

  const renderPanelContent = () => {
    if (vegaPermissionError?.message?.includes('access denied')) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" padding="16" cssClass={styles.centeredAlertBox}>
          <Alert status="error">
            <Heading slot="title">Permission denied</Heading>
            <Text>
              You don't have permission to use {AI_BOT_NAME}. A workspace admin must grant you access to the{' '}
              <code>talkToVega</code> action on the <code>proj/default:vega/*</code> resource.{' '}
              <a
                href={getDocumentationUrl('home/getting-started/vega#roles-and-rbac')}
                target="_blank"
                rel="noopener noreferrer"
              >
                Read more here
              </a>
              .
            </Text>
          </Alert>
        </Box>
      );
    }

    if (showHistory) {
      return (
        <ConversationHistoryView
          onOpenConversation={handleOpenHistoryConversation}
          currentUserId={profile._id}
          currentUserEmail={profile.email}
          aiChatLimitData={aiChatLimitData}
          usageHref={toDiagnosticUsage().pathname}
        />
      );
    }

    if (conversationNotShared) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" padding="16" cssClass={styles.centeredAlertBox}>
          <Alert status="warning">
            <Heading slot="title">Conversation not available</Heading>
            <Text>
              This conversation has not been shared by its owner. The owner can share it using the &quot;Share&quot;
              button in the header of the {AI_BOT_NAME} panel.
            </Text>
          </Alert>
        </Box>
      );
    }

    const artifactGraphEnabled = vegaArtifactGraph();

    const insights = (
      <AgentInsights
        onClearRef={clearRef}
        onFocusRef={focusRef}
        onClearPromptRef={clearPromptRef}
        isLoadingConversation={isLoadingConversation}
        aiChatLimitData={aiChatLimitData}
        simplifiedFooter={nonGenAiSkill}
        agentPromptMutation={agentPromptMutation}
        sending={sending}
        enableAgentResponseSelector={enableAgentResponseSelector}
      />
    );

    if (!artifactGraphEnabled) {
      return insights;
    }

    // The conversation artifact bar now lives on the full-page conversation
    // view (VegaConversationPage), not the panel. The panel keeps the
    // ArtifactLookupProvider so inline artifact chips in messages still resolve.
    // Provider's `artifacts` is empty for now — every inline chip flows through
    // lazy-fetch; the pre-resolved hydration path lights up once that source lands.
    return (
      <ArtifactLookupProvider
        projectKey={projectKey}
        artifacts={EMPTY_ARTIFACT_LOOKUP}
        canonicalize={canonicalizeArtifactExternalId}
      >
        {insights}
      </ArtifactLookupProvider>
    );
  };

  return createPortal(
    <>
      <Dialog
        store={dialogStore}
        modal={false}
        autoFocusOnShow={false}
        hideOnEscape
        hideOnInteractOutside
        backdrop={false}
        className={styles.panel}
        ref={dialogRef}
        id="agent-insights-panel"
      >
        <div ref={dragHandleRef} className={styles.panelDragHandle} />
        <Box display="flex" alignItems="center" justifyContent="space-between" py="12" px="12" borderBottom="divider">
          {renderPanelHeader()}
        </Box>
        <Box height="full" overflow="hidden">
          {renderPanelContent()}
        </Box>
      </Dialog>
      {!nonGenAiSkill && showAiRedirectModal && (
        <EnableSettingModal isOpen={showAiRedirectModal} onOpenChange={setShowAiRedirectModal} product="Vega chat" />
      )}
      <ContextualFeedbackModal
        flagKey="feedback-recipient-ai-agent-conversation"
        isOpen={showingFeedback}
        onOpenChange={setShowingFeedback}
        className={styles.feedbackModal}
      />
    </>,
    document.body,
  );
};
